'use client';
import React, { useState } from 'react';
import { Label } from './ui/Label';
import { Input } from './ui/Input';
import { PhoneInput } from './ui/PhoneInput';
import { cn } from '@/lib/utils';
import Niro from './Niro';
import { motion, AnimatePresence } from 'framer-motion';
import { CONFIG } from '@/lib/config';
import { trackMetaPixel } from '@/lib/metaPixel';

type FormField = {
  type: string;
  label: string;
  required: boolean;
};

type FormFields = {
  [key: string]: FormField;
};

export function SignupForm({ id }: { id: keyof typeof CONFIG }) {
  const [niroData, setNiroData] = useState<{
    userName: string;
    userEmail: string;
    userCompany: string;
    userWebsite: string;
    userPhone?: string;
    userPostcode?: string;
  } | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    trackMetaPixel('Lead');

    setNiroData({
      userName: `${formData.get('firstname')} ${formData.get('lastname')}`,
      userEmail: formData.get('email') as string,
      userCompany: formData.get('company') as string,
      userWebsite: formData.get('website') as string,
      userPhone: formData.get('phone') as string,
      userPostcode: formData.get('postcode') as string,
    });
  };

  const formFields = CONFIG[id].formFields as FormFields;

  const renderFormFields = () => {
    const fields = Object.entries(formFields);
    const nameFields = fields.filter(([key]) => key === 'firstname' || key === 'lastname');
    const otherFields = fields.filter(([key]) => key !== 'firstname' && key !== 'lastname');

    return (
      <div className="mb-6">
        <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 mb-4">
          {nameFields.map(([key, field]) => (
            <LabelInputContainer key={key}>
              <Label htmlFor={key}>
                {field.label}
                {!field.required && (
                  <span className="text-stone text-sm font-normal"> (Optional)</span>
                )}
              </Label>
              <Input
                required={field.required}
                id={key}
                name={key}
                placeholder={
                  field.required
                    ? `Enter your ${field.label.toLowerCase()}`
                    : `Enter your ${field.label.toLowerCase()} (optional)`
                }
                type={field.type}
              />
            </LabelInputContainer>
          ))}
        </div>
        {otherFields.map(([key, field]) => (
          <LabelInputContainer key={key} className="mb-4">
            <Label htmlFor={key}>
              {field.label}
              {!field.required && (
                <span className="text-stone text-sm font-normal"> (Optional)</span>
              )}
            </Label>
            {field.type === 'tel' ? (
              <PhoneInput required={field.required} id={key} name={key} />
            ) : (
              <Input
                required={field.required}
                id={key}
                name={key}
                placeholder={
                  field.required
                    ? `Enter your ${field.label.toLowerCase()}`
                    : `Enter your ${field.label.toLowerCase()} (optional)`
                }
                type={field.type}
              />
            )}
          </LabelInputContainer>
        ))}
      </div>
    );
  };

  return (
    <AnimatePresence mode="wait">
      {niroData ? (
        <motion.div
          key="niro-component"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Niro data={niroData} id={id} />
        </motion.div>
      ) : (
        <motion.div
          key="signup-form"
          className="max-w-md w-full mx-auto"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="mb-8">
            <h2 className="font-serif text-2xl text-coal tracking-tight">Get Started</h2>
            <div className="w-8 h-[2px] bg-dune mt-3 mb-4" />
            <p className="text-ash/70 text-[0.9rem] leading-relaxed max-w-sm">
              Share a few details and we&apos;ll personalize your demo experience.
            </p>
          </div>

          <form className="space-y-0" onSubmit={handleSubmit}>
            {renderFormFields()}
            <button
              className="relative block w-full mt-6 bg-coal text-salt rounded-lg h-11 font-medium text-sm tracking-wide hover:bg-ash active:scale-[0.99] transition-all duration-200 shadow-sm"
              type="submit"
            >
              Start Your Demo
              <span className="inline-block ml-1.5 transition-transform group-hover:translate-x-0.5">
                &rarr;
              </span>
            </button>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const LabelInputContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <div className={cn('flex flex-col space-y-2 w-full', className)}>{children}</div>;
};
